import { Component, forwardRef, inject, input, OnInit, output, ViewChild } from '@angular/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { getKind } from '../../../core/util';
import { TranslationService } from '../../../core/services/translationService';
import { ExerciseKindMap } from '../../../core/types';
import { AppSelectors } from '../../../app.state';
import { Store } from '@ngxs/store';
import { TranslocoModule } from '@jsverse/transloco';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

interface Kind {
  value: string
  option: string
}

@Component({
  selector: 'pixl-type-cb',
  imports: [
    MatSelectModule,
    MatIcon,
    TranslocoModule
  ],
  templateUrl: './type-cb.component.html',
  styleUrl: './type-cb.component.scss', providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeCBComponent),
      multi: true
    }
  ]
})
export class TypeCBComponent implements ControlValueAccessor, OnInit {
  @ViewChild('typeCB') typeCB!: MatSelect;

  private readonly store = inject(Store);
  private translationService = inject(TranslationService);
  private appState$ = this.store.select(AppSelectors.appState);

  public matLabelText = input<string>('');
  public matIcon = input();
  public withoutNone = input(true);
  public allowEmpty = input(true);
  public resetOnSelect = input(false);
  public onStateChange = output<string>();
  public kinds: Kind[] = [];

  // CVA variables
  public value = '';
  public disabled = false;

  ngOnInit(): void {
    this.appState$.subscribe(({ isLoaded }) => {
      if (isLoaded) {
        const allKinds: Kind[] = [];

        for (const [, value] of Object.entries(ExerciseKindMap)) {
          allKinds.push({
            value: value.toString(),
            option: getKind(this.translationService, value.toString())
          });
        }

        this.kinds = allKinds
          .filter(kind => {
            if (this.withoutNone() && kind.value === ExerciseKindMap.NONE.toString()) {
              return false;
            }
            return true;
          })
          .sort((a, b) => {
            if (a.value === ExerciseKindMap.NONE.toString()) return -1;
            if (b.value === ExerciseKindMap.NONE.toString()) return 1;
            return a.option.localeCompare(b.option);
          });
      }
    });
  }

  public onChangeEvent(ev: string): void {
    this.onStateChange.emit(ev);

    this.onChange(ev);
    this.onTouched();

    if (this.resetOnSelect()) {
      this.value = '';
      this.typeCB.value = '';

      this.onChange('');
    }
  }

  // CVA methods
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: string): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
